import React from 'react';
import { MapPin, Clock, Users, Star, Award } from 'lucide-react';

import '../styles/about.css';

const reviews = [
  {
    id: 1,
    name: "Sarah Ahmed",
    image: "/api/placeholder/100/100",
    rating: 5,
    review: "Amazing platform! Makes finding Halal restaurants so much easier.",
    location: "New York, USA"
  },
  {
    id: 2,
    name: "Mohammed Ali",
    image: "/api/placeholder/100/100",
    rating: 5,
    review: "Great experience using halal-bees. Found many new restaurants!",
    location: "London, UK"
  },
  {
    id: 3,
    name: "Fatima Hassan",
    image: "/api/placeholder/100/100",
    rating: 4,
    review: "Very helpful in finding authentic Halal food in my area.",
    location: "Toronto, Canada"
  },
  {
    id: 4,
    name: "Ahmad Khan",
    image: "/api/placeholder/100/100",
    rating: 5,
    review: "The verification system gives me confidence in the restaurants.",
    location: "Dubai, UAE"
  },
  {
    id: 5,
    name: "Aisha Malik",
    image: "/api/placeholder/100/100",
    rating: 5,
    review: "Best platform for finding Halal dining options!",
    location: "Sydney, Australia"
  }
];

const About = () => {
  return (
    <div className="about-page">
      {/* Hero Section with Gradient */}
      <div className="hero-section text-white py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h1 className="display-4 fw-bold mb-4">About halal-bees</h1>
              <p className="lead mb-0">Your Trusted Platform for Halal Dining Experiences</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="container py-5">
        <div className="row align-items-center">
          <div className="col-lg-6 mb-4 mb-lg-0">
            <h2 className="fw-bold mb-4">Our Mission</h2>
            <p className="lead text-muted">
              We strive to connect the Muslim community with authentic Halal restaurants, making it easier to discover, book, and enjoy quality dining experiences.
            </p>
            <div className="mt-4">
              <div className="d-flex align-items-center mb-3">
                <div className="feature-icon">
                  <MapPin size={24} />
                </div>
                <div className="ms-3">
                  <h5 className="mb-1">Easy Discovery</h5>
                  <p className="mb-0 text-muted">Find Halal restaurants near you with ease</p>
                </div>
              </div>
              <div className="d-flex align-items-center mb-3">
                <div className="feature-icon">
                  <Clock size={24} />
                </div>
                <div className="ms-3">
                  <h5 className="mb-1">Quick Booking</h5>
                  <p className="mb-0 text-muted">Instant table reservations at your favorite spots</p>
                </div>
              </div>
              <div className="d-flex align-items-center">
                <div className="feature-icon">
                  <Star size={24} />
                </div>
                <div className="ms-3">
                  <h5 className="mb-1">Verified Reviews</h5>
                  <p className="mb-0 text-muted">Real feedback from our community</p>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="about-image rounded-4 shadow-lg"></div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="stats-section text-white py-5">
        <div className="container">
          <div className="row text-center g-4">
            <div className="col-md-4">
              <h2 className="fw-bold mb-2">1000+</h2>
              <p className="mb-0">Restaurants</p>
            </div>
            <div className="col-md-4">
              <h2 className="fw-bold mb-2">50,000+</h2>
              <p className="mb-0">Happy Customers</p>
            </div>
            <div className="col-md-4">
              <h2 className="fw-bold mb-2">100+</h2>
              <p className="mb-0">Cities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="container py-5">
        <h2 className="text-center fw-bold mb-5">Our Team</h2>
        <div className="row g-4">
          {[1, 2, 3].map((member) => (
            <div key={member} className="col-md-4">
              <div className="card team-card h-100">
                <div className="card-body text-center p-4">
                  <div className="team-avatar mb-3">
                    <Users size={40} />
                  </div>
                  <h5 className="card-title mb-1">Team Member {member}</h5>
                  <p className="text-muted mb-3">Position</p>
                  <p className="card-text">Dedicated to providing the best Halal dining experience.</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Vision Section */}
      <div className="vision-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="vision-content">
                <h2 className="section-title">Our Vision</h2>
                <p className="lead mb-4">
                  To become the world's most trusted platform for discovering authentic Halal dining experiences.
                </p>
                <div className="vision-points">
                  <div className="point">
                    <div className="point-icon">
                      <Award />
                    </div>
                    <div className="point-text">
                      <h5>Quality Assurance</h5>
                      <p>Strict verification process for all restaurants</p>
                    </div>
                  </div>
                  <div className="point">
                    <div className="point-icon">
                      <Users />
                    </div>
                    <div className="point-text">
                      <h5>Community Building</h5>
                      <p>Connecting Muslims with trusted dining options</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="vision-image rounded-4 shadow-lg"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="testimonials-section py-5">
        <div className="container">
          <h2 className="text-center mb-5">What Our Users Say</h2>
          <div className="row g-4">
            {[
              {
                name: "Sarah A.",
                role: "Food Enthusiast",
                quote: "Finally, a platform that understands the needs of Muslim diners!"
              },
              {
                name: "Mohammed R.",
                role: "Restaurant Owner",
                quote: "halal-bees has helped us connect with our target audience effectively."
              },
              {
                name: "Aisha K.",
                role: "Regular User",
                quote: "Makes finding Halal restaurants so much easier. Love the verification system!"
              }
            ].map((testimonial, index) => (
              <div key={index} className="col-md-4">
                <div className="testimonial-card">
                  <div className="quote-icon">❝</div>
                  <p className="quote-text">{testimonial.quote}</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="author-info">
                      <h5>{testimonial.name}</h5>
                      <p>{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="timeline-section py-5">
        <div className="container">
          <h2 className="text-center mb-5">Our Journey</h2>
          <div className="timeline">
            {[
              {
                year: "2021",
                title: "The Beginning",
                description: "halal-bees was founded with a vision to revolutionize Halal dining"
              },
              {
                year: "2022",
                title: "Rapid Growth",
                description: "Expanded to 50+ cities and verified 500+ restaurants"
              },
              {
                year: "2023",
                title: "Community Focus",
                description: "Launched community features and enhanced verification system"
              }
            ].map((milestone, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h3>{milestone.year}</h3>
                  <h4>{milestone.title}</h4>
                  <p>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="newsletter-section text-white py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="mb-4">Stay Updated</h2>
              <p className="lead mb-4">Subscribe to our newsletter for updates on new restaurants and features.</p>
              <div className="input-group">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder="Enter your email"
                />
                <button className="btn btn-primary">Subscribe</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Reviews Section */}
      <div className="reviews-section py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">Customer Reviews</h2>
            <p className="text-muted">What our community says about us</p>
          </div>
          
          <div className="reviews-slider">
            <div className="reviews-track">
              {[...reviews, ...reviews].map((review, index) => (
                <div key={index} className="review-card">
                  <div className="review-rating mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={20} className="star-icon" fill="#FFD700" color="#FFD700" />
                    ))}
                  </div>
                  <p className="review-text">{review.review}</p>
                  <div className="reviewer-info">
                    <img src={review.image || "/placeholder.svg"} alt={review.name} className="reviewer-img" />
                    <div>
                      <h5 className="reviewer-name">{review.name}</h5>
                      <p className="reviewer-location">{review.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
